import {
	Badge,
	Combobox,
	Field,
	IconButton,
	Input,
	Spinner,
	Text,
	createListCollection,
} from '@components'
import { api } from '@here/api'
import { debounce } from '@solid-primitives/scheduled'
import { CheckIcon, ChevronsUpDownIcon, XIcon } from 'lucide-solid'
import { For, Show, createMemo, createResource, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { Box } from 'styled-system/jsx'
import { useFilters } from '../state/filter'

const searchSetups = async (query: string) => {
	if (!query) return []

	const { data, error } = await api()
		.from('setup')
		.select('symbol')
		.ilike('symbol', `%${query}%`)
		.limit(10)

	if (error) {
		console.error('Supabase fetch error:', error)
		return []
	}

	return data.map((row) => ({
		label: row.symbol,
		value: row.symbol,
	}))
}

export default function SymbolSearchCombobox() {
	const { setSymbols, filters } = useService(useFilters)()
	const [inputValue, setInputValue] = createSignal('')
	const debouncedSetInputValue = debounce(setInputValue, 300)

	const selected = () => filters().symbols

	// Resource for fetching symbols
	const [symbols] = createResource(inputValue, searchSetups)

	const collection = createMemo(() =>
		createListCollection({
			items: symbols.state === 'ready' ? symbols() : [],
		}),
	)

	// Remove item from selected list
	const removeValue = (val: string) => setSymbols(selected().filter((v) => v !== val))

	return (
		<Field.Root>
			<Show when={selected().length > 0}>
				<Box
					mb="2"
					display="flex"
					flexWrap="wrap"
					gap="2"
				>
					<For each={selected()}>
						{(val) => (
							<Badge>
								{val}
								<IconButton
									aria-label="Remove"
									variant="ghost"
									height="3"
									width="3"
									onClick={() => removeValue(val)}
								>
									<XIcon />
								</IconButton>
							</Badge>
						)}
					</For>
				</Box>
			</Show>

			<Combobox.Root
				width="2xs"
				onInputValueChange={(value) => debouncedSetInputValue(value.inputValue)}
				value={selected()}
				onValueChange={({ value }) => setSymbols(value)}
				collection={collection()}
				multiple
			>
				<Combobox.Label>Symbols</Combobox.Label>
				<Combobox.Control>
					<Combobox.Input
						placeholder="Search symbols..."
						asChild={(inputProps) => <Input {...inputProps()} />}
					/>
					<Combobox.Trigger
						asChild={(triggerProps) => (
							<IconButton
								variant="link"
								aria-label="open"
								size="xs"
								{...triggerProps()}
							>
								<ChevronsUpDownIcon />
							</IconButton>
						)}
					/>
				</Combobox.Control>
				<Combobox.Positioner>
					<Combobox.Content>
						<Combobox.ItemGroup>
							<Show when={symbols.loading}>
								<Spinner />
							</Show>
							<Show when={!symbols.loading && symbols()?.length === 0}>
								<Text>No matches</Text>
							</Show>
							<For each={symbols()}>
								{(item) => (
									<Combobox.Item item={item}>
										<Combobox.ItemText>{item.label}</Combobox.ItemText>
										<Combobox.ItemIndicator>
											<CheckIcon />
										</Combobox.ItemIndicator>
									</Combobox.Item>
								)}
							</For>
						</Combobox.ItemGroup>
					</Combobox.Content>
				</Combobox.Positioner>
			</Combobox.Root>
		</Field.Root>
	)
}
